import { useEffect, useState } from "react";
import api, { money, moneyPrecise, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CategorySelect } from "@/components/CategorySelect";
import { Plus, PencilSimple, Trash, Stack, ArrowsMerge } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { category: "", description: "", unit: "", default_budget: 0, vendor_id: "", notes: "" };

export default function CostLibrary() {
  const { user } = useAuth();
  const isManager = ["owner", "admin"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [merging, setMerging] = useState(false);

  const load = () => {
    api.get("/cost-library").then((r) => setItems(r.data));
    api.get("/vendors").then((r) => setVendors(r.data));
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const vendorName = (vid) => vendors.find((v) => v.id === vid)?.name || "—";

  const dedupe = async () => {
    setMerging(true);
    try {
      const { data } = await api.post("/cost-library/dedupe");
      if (data.removed) toast.success(`Merged ${data.removed} duplicate${data.removed === 1 ? "" : "s"} into ${data.merged_groups} item${data.merged_groups === 1 ? "" : "s"} (averaged price)`);
      else toast.message("No duplicates found — your library is tidy");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setMerging(false); }
  };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it.id);
    setForm({ category: it.category || "", description: it.description || "", unit: it.unit || "", default_budget: it.default_budget || 0, vendor_id: it.vendor_id || "", notes: it.notes || "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, default_budget: Number(form.default_budget), vendor_id: form.vendor_id === "none" ? "" : form.vendor_id };
    try {
      if (editing) await api.put(`/cost-library/${editing}`, payload);
      else await api.post("/cost-library", payload);
      toast.success(editing ? "Item updated" : "Item added to library");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (iid) => { await api.delete(`/cost-library/${iid}`); toast.success("Item deleted"); load(); };

  return (
    <div>
      <PageHeader label="Estimating" title="Cost Library">
        {isManager && items.length > 0 && (
          <Button variant="outline" onClick={dedupe} disabled={merging} className="rounded-none" data-testid="merge-duplicates-button">
            <ArrowsMerge size={16} className="mr-1" /> {merging ? "Merging…" : "Merge duplicates"}
          </Button>
        )}
        <Button onClick={openNew} className="rounded-none" data-testid="new-library-item-button"><Plus size={16} className="mr-1" /> New Item</Button>
      </PageHeader>
      <div className="p-4 sm:p-8">
        <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
          Save your standard trade costs once, then drop them into any project from a phase's <strong>Add from Library</strong> action — turning duplicate-template builds into a full reusable estimating catalog.
        </p>
        {items.length === 0 ? (
          <div className="border border-dashed border-border p-16 text-center" data-testid="library-empty">
            <Stack size={40} className="mx-auto text-muted-foreground mb-4" />
            <p className="font-heading text-xl font-bold mb-2">No library items yet</p>
            <p className="text-muted-foreground text-sm">Add typical costs like "Framing per sqft" or "Concrete per yard" to reuse across projects.</p>
          </div>
        ) : (
          <>
          {/* Mobile cards */}
          <div className="md:hidden space-y-3 stagger">
            {items.map((it) => (
              <div key={it.id} data-testid={`library-card-${it.id}`} className="border border-border p-4 bg-white">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{it.description}</div>
                    <div className="text-xs text-muted-foreground">{it.category}{it.unit ? ` · ${it.unit}` : ""}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => openEdit(it)} className="text-muted-foreground hover:text-primary"><PencilSimple size={18} /></button>
                    {isManager && <button onClick={() => remove(it.id)} className="text-muted-foreground hover:text-destructive"><Trash size={18} /></button>}
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <Sparkline points={it.price_history} />
                  <div className="text-right">
                    <span className="font-mono-fig font-semibold tabular-nums">{moneyPrecise(it.default_budget)}</span>
                    {it.count > 1 && <span className="block text-[10px] text-muted-foreground">avg of {it.count}×</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Desktop table */}
          <div className="border border-border hidden md:block reveal">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Description</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Default Vendor</TableHead>
                  <TableHead>Trend</TableHead>
                  <TableHead className="text-right">Unit Cost</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.id} data-testid={`library-row-${it.id}`} className="hover:bg-secondary transition-colors duration-200">
                    <TableCell className="font-medium">{it.description}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{it.category || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{it.unit || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{vendorName(it.vendor_id)}</TableCell>
                    <TableCell data-testid={`library-trend-${it.id}`}><Sparkline points={it.price_history} /></TableCell>
                    <TableCell className="text-right font-mono-fig tabular-nums font-semibold">
                      {moneyPrecise(it.default_budget)}
                      {it.count > 1 && <span className="block text-[10px] text-muted-foreground font-normal">avg of {it.count}×</span>}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <button onClick={() => openEdit(it)} data-testid={`edit-library-${it.id}`} className="text-muted-foreground hover:text-primary mr-3"><PencilSimple size={16} /></button>
                      {isManager && (
                        <button onClick={() => remove(it.id)} data-testid={`delete-library-${it.id}`} className="text-muted-foreground hover:text-destructive"><Trash size={16} /></button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          </>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Library Item" : "New Library Item"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="library-form">
            <div>
              <Label className="label-caps">Description</Label>
              <Input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="library-description-input" className="mt-2" placeholder="Framing labor" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-caps">Category / Trade</Label>
                <CategorySelect categories={categories} value={form.category}
                  onChange={(v) => setForm({ ...form, category: v })}
                  testId="library-category-select" placeholder="Select category" />
              </div>
              <div>
                <Label className="label-caps">Unit</Label>
                <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="library-unit-input" className="mt-2" placeholder="per sqft, per yard, lump sum" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-caps">Unit Cost ($)</Label>
                <Input type="number" step="0.01" value={form.default_budget} onChange={(e) => setForm({ ...form, default_budget: e.target.value })} data-testid="library-budget-input" className="mt-2 font-mono-fig" />
              </div>
              <div>
                <Label className="label-caps">Default Vendor</Label>
                <Select value={form.vendor_id || "none"} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                  <SelectTrigger className="mt-2 rounded-none" data-testid="library-vendor-select"><SelectValue placeholder="Vendor" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="label-caps">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="library-submit-button" className="rounded-none">{saving ? "Saving…" : editing ? "Update" : "Add to Library"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Sparkline({ points }) {
  const vals = (points || []).map((p) => Number(p.amount) || 0).filter((v) => v > 0);
  if (vals.length < 2) return <span className="text-xs text-muted-foreground">—</span>;
  const w = 72, h = 22, pad = 3;
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = max - min || 1;
  const step = (w - pad * 2) / (vals.length - 1);
  const coords = vals.map((v, i) => [pad + i * step, h - pad - ((v - min) / span) * (h - pad * 2)]);
  const d = coords.map((c, i) => `${i === 0 ? "M" : "L"}${c[0].toFixed(1)},${c[1].toFixed(1)}`).join(" ");
  const rising = vals[vals.length - 1] >= vals[0];
  const color = rising ? "#DC2626" : "#059669";
  const [lx, ly] = coords[coords.length - 1];
  const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
  return (
    <svg width={w} height={h} className="inline-block align-middle" role="img"
      aria-label="price trend" data-testid="price-sparkline">
      <title>{`${vals.length} observations · low ${fmt(min)} · high ${fmt(max)} · ${rising ? "trending up" : "trending down"}`}</title>
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lx} cy={ly} r="2.2" fill={color} />
    </svg>
  );
}
